// Driver auth helper for nf-api calls.
//
// The JWT issued by /icollect/driverLogin is persisted to sessionStorage
// ("nfToken") at login and attached as `Authorization: Bearer <jwt>` on every
// subsequent API request, so the driver app keeps working once the API turns on
// enforcement (AUTH_ENFORCED=true). While the API still runs dark the header is
// simply ignored — no behavior change today.
//
// Reads from sessionStorage (not the Vuex store) to stay decoupled and avoid
// circular imports from composables.

export function getNfToken() {
    try {
        return sessionStorage.getItem("nfToken") || null;
    } catch (e) {
        return null;
    }
}

// Standard JSON headers + Authorization when a token is present. `extra` lets a
// call site add/override headers.
export function authHeaders(extra = {}) {
    const token = getNfToken();
    return {
        "Content-Type": "application/json",
        accept: "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...extra,
    };
}

// Force a fresh login. Used when the API rejects a call with 401 — e.g. a driver
// is still on a screen with a pre-JWT session at the moment auth enforcement is
// switched on server-side. Clears the stale session and returns to the login
// route. The router is imported lazily so this module stays free of the
// router <-> views import cycle.
let _reloggingIn = false;
export function forceReLogin() {
    if (_reloggingIn) return; // guard against multiple concurrent 401s
    _reloggingIn = true;
    try {
        sessionStorage.clear();
    } catch (e) {
        /* ignore */
    }
    import("@/router")
        .then(({ default: router }) => {
            if (router.currentRoute.value.name !== "login") {
                router.replace({ name: "login" });
            }
            _reloggingIn = false;
        })
        .catch(() => {
            if (typeof window !== "undefined") window.location.assign("/");
        });
}

// True when the API rejected the request because auth is required/invalid.
export function isAuthFailure(response) {
    return !!response && (response.status === 401 || response.status === 403);
}
